/**
 * The platform console's pages, grouped as its sidebar draws them (R12,
 * screens 37–45):
 *
 *   Customers    Accounts · Plans
 *   Growth       Marketing · Site content
 *   Operations   Staff · Services · Rules
 *   Audit        Platform audit log
 *
 * ONE list, read by the sidebar and the phone drawer (PlatformLayout) and by
 * the ⌘K index (commandIndex.ts), so the three can never disagree — the
 * pattern settingsNav.ts set for account settings in R10. Every rung of the
 * platform ladder sees every page: each page gates its own writes, and the
 * server refuses each route either way.
 *
 * Accounts is `/platform` itself (`end`), and stays lit on one account's
 * billing page, which lives under `/platform/accounts/`. Services is the
 * seven-tab settings page at `/platform/settings`; its old name, "Settings",
 * stays one of its ⌘K keywords. Storage is one of those tabs and has no
 * sidebar row — ⌘K alone keeps a direct way to it.
 */
export interface PlatformPage {
  to: string;
  label: string;
  /** NavLink's `end`: lit on this exact path only (plus `also`). */
  end?: boolean;
  /** Path prefixes on which the row stays lit too (SideNav's `also`). */
  also?: string[];
  /** Extra words ⌘K matches on (a page's old name among them). */
  keywords?: string;
}
export interface PlatformGroup {
  label: string;
  items: PlatformPage[];
}

export const PLATFORM_NAV: readonly PlatformGroup[] = [
  {
    label: 'Customers',
    items: [
      { to: '/platform', label: 'Accounts', end: true, also: ['/platform/accounts'], keywords: 'customers tenants billing health mrr' },
      { to: '/platform/plans', label: 'Plans', keywords: 'catalogue pricing prices limits features stripe' },
    ],
  },
  {
    label: 'Growth',
    items: [
      { to: '/platform/marketing', label: 'Marketing', keywords: 'campaigns email prospects' },
      { to: '/platform/content', label: 'Site content', keywords: 'releases faq public site home features screenshots look favicon publish' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/platform/staff', label: 'Staff', keywords: 'people roles admins team' },
      { to: '/platform/settings', label: 'Services', keywords: 'settings stripe email smtp storage captcha domains ai urls' },
      // The numbers the service runs on — trial length, graces, caps, throttles, the role ladders.
      { to: '/platform/rules', label: 'Rules', keywords: 'trial grace dunning limits caps throttles password roles defaults' },
    ],
  },
  {
    label: 'Audit',
    items: [
      { to: '/platform/audit', label: 'Platform audit log', keywords: 'audit trail history activity masquerade support' },
    ],
  },
];

/**
 * Pages ⌘K offers that the sidebar does not, each listed right after the
 * sidebar row it belongs to.
 */
const PALETTE_ONLY: readonly (PlatformPage & { after: string })[] = [
  { to: '/platform/settings?tab=storage', label: 'Storage', keywords: 'uploads bucket s3 files services', after: '/platform/settings' },
];

/** Every page ⌘K lists for platform staff, in the sidebar's order. */
export function platformPalettePages(): PlatformPage[] {
  const out: PlatformPage[] = [];
  for (const page of PLATFORM_NAV.flatMap((g) => g.items)) {
    out.push(page);
    for (const extra of PALETTE_ONLY) if (extra.after === page.to) out.push(extra);
  }
  return out;
}
